import { ComponentProps, useState } from "react";
import { useInView } from "react-intersection-observer";
import { QueryBody } from "@api/internal/utils/types";
import Card from "@particles/card/Card";
import { getTemplateGProps } from "@particles/card/templates/TemplateG/utils/helpers";
import { SanitizedFeedSectionArticle } from "@typings/Cue";
import { tryCatch } from "@typings/TryCatch";
import axios from "axios";

type useLoadMoreProps = {
  /** The initial list of articles to display. */
  initialList: ComponentProps<typeof Card>[];
} & Omit<QueryBody, "page">;

/**
 * Custom hook to manage loading more stories for listing pages.
 * This hook handles the logic for loading more stories when the user scrolls to the bottom of the page.
 * It uses the Intersection Observer API to detect when the user has scrolled to the bottom and fetches more stories accordingly.
 */
export const useLoadMore = ({ initialList, size = 10 }: useLoadMoreProps) => {
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [loadMoreData, setLoadMoreData] = useState<
    ComponentProps<typeof Card>[]
  >([]);

  const handleLoadMore = async (data: ComponentProps<typeof Card>[]) => {
    const page = [...data, ...loadMoreData].length / size + 1;

    const articles = await fetchListingStories({
      page,
      size,
    });

    if (articles.length === 0) {
      setHasMoreStories(false);

      return;
    }

    const articleProps = getTemplateGProps(articles, false, "hub");

    setLoadMoreData([...loadMoreData, ...articleProps]);

    if (articles.length < size) {
      setHasMoreStories(false);
    }
  };

  const { ref } = useInView({
    threshold: 0,
    onChange: (inView) => {
      if (!inView) return;
      if (!hasMoreStories) return;
      if (initialList.length < size) return;

      handleLoadMore(initialList);
    },
  });

  return {
    ref,
    handleLoadMore,
    hasMoreStories,
    loadMoreData,
  };
};

/**
 * Fetches more stories by kicker.
 *
 * @param page - The page number to fetch.
 * @param size - The number of articles per page.
 * @returns A promise that resolves to the fetched SanitizedFeedSectionArticle[].
 */
const fetchListingStories = async ({
  page,
  size,
}: QueryBody): Promise<SanitizedFeedSectionArticle[]> => {
  const payload: QueryBody = {
    kicker: "Branded Content",
    size,
    page,
  };

  const request = axios.post<SanitizedFeedSectionArticle[]>(
    "/_plat/api/v1/more-stories-by-kicker",
    payload
  );
  const { data, error } = await tryCatch(request);

  if (error) return [];

  return data.data;
};
